import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Image } from 'antd';
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  BarcodeOutlined,
  ContactsOutlined,
  FileDoneOutlined,
  ShopOutlined,
  InboxOutlined,
  TagsOutlined,
  FileTextOutlined,
  BarChartOutlined,
  MenuOutlined,
  CloseOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { getCurrentUser, hasAnyPermission, PERMISSIONS, RBACService, INVENTORY_ACCESS_PERMISSIONS } from '../utils/rbac';
import { useCompanyLogo } from '../hooks/useCompanyLogo';
import './AdminSidebar.css';

const nacLogoUrl = `${process.env.PUBLIC_URL || ''}/nac_logo_tight_circle.png`;

interface SidebarProps {
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
  isMobile?: boolean;
}

interface MenuItem {
  key: string;
  icon?: React.ReactNode;
  label: string;
  permissions?: string[];
  children?: MenuItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed: controlledCollapsed, onCollapse, isMobile: isMobileProp }) => {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [isMobileLocal, setIsMobileLocal] = useState(false);
  const isControlled = controlledCollapsed !== undefined;
  const collapsed = isControlled ? controlledCollapsed : internalCollapsed;
  const isMobile = isMobileProp !== undefined ? isMobileProp : isMobileLocal;
  const setCollapsed = useCallback((value: boolean) => {
    if (!isControlled) setInternalCollapsed(value);
    onCollapse?.(value);
  }, [isControlled, onCollapse]);
  const [currentUser, setCurrentUser] = useState(getCurrentUser());
  const companyLogo = useCompanyLogo(currentUser?.id ?? null);
  const navigate = useNavigate();
  const location = useLocation();
  const roleLabel = (currentUser?.roleName || 'User').toString();
  /** Fixed portal name; subtitle is role only (current page is obvious from the menu). */
  const panelTitle = 'Company Portal';
  const sectionSubtitle = roleLabel;

  useEffect(() => {
    const isDebugMode = process.env.REACT_APP_DEBUG === 'true';
    if (isDebugMode && !getCurrentUser()) {
      RBACService.setCurrentUser({
        id: '1',
        email: 'debug@test.com',
        name: 'Debug User',
        roleId: 1,
        roleName: 'Admin',
        permissions: Object.values(PERMISSIONS),
      });
      setCurrentUser(getCurrentUser());
    }
    const checkScreenSize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobileLocal(mobile);
      if (mobile && !isControlled) setInternalCollapsed(true);
    };
    const updateCurrentUser = () => setCurrentUser(getCurrentUser());
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    window.addEventListener('storage', updateCurrentUser);
    window.addEventListener('sessionRestored', updateCurrentUser);
    return () => {
      window.removeEventListener('resize', checkScreenSize);
      window.removeEventListener('storage', updateCurrentUser);
      window.removeEventListener('sessionRestored', updateCurrentUser);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allMenuItems: MenuItem[] = [
    { key: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard', permissions: [PERMISSIONS.DASHBOARD_VIEW] },
    { key: '/sales', icon: <ShoppingCartOutlined />, label: 'Sales reports', permissions: [PERMISSIONS.POS_MANAGE, PERMISSIONS.REPORT_VIEW] },
    { key: '/pos', icon: <BarcodeOutlined />, label: 'POS', permissions: [PERMISSIONS.POS_MANAGE] },
    { key: '/purchase-orders', icon: <ShopOutlined />, label: 'Purchase Orders', permissions: [PERMISSIONS.PO_READ, PERMISSIONS.PO_CREATE, PERMISSIONS.PO_UPDATE] },
    // Show GRPO only to users who can actually receive goods.
    { key: '/grpo', icon: <FileDoneOutlined />, label: 'GRPO', permissions: [PERMISSIONS.GOODS_RECEIVE] },
    { key: '/inventory', icon: <InboxOutlined />, label: 'Inventory', permissions: INVENTORY_ACCESS_PERMISSIONS },
    { key: '/products', icon: <TagsOutlined />, label: 'Products', permissions: [PERMISSIONS.PRODUCT_READ, PERMISSIONS.PRODUCT_CREATE, PERMISSIONS.PRODUCT_UPDATE] },
    { key: '/vendors', icon: <ContactsOutlined />, label: 'Vendors', permissions: [PERMISSIONS.VENDOR_READ, PERMISSIONS.VENDOR_CREATE, PERMISSIONS.VENDOR_UPDATE, PERMISSIONS.VENDOR_DELETE, PERMISSIONS.VENDOR_PRODUCT_MANAGE] },
    { key: '/vendor-products', icon: <LinkOutlined />, label: 'Vendor products', permissions: [PERMISSIONS.VENDOR_READ, PERMISSIONS.VENDOR_CREATE, PERMISSIONS.VENDOR_UPDATE, PERMISSIONS.VENDOR_DELETE, PERMISSIONS.VENDOR_PRODUCT_MANAGE] },
    { key: '/documents', icon: <FileTextOutlined />, label: 'Documents', permissions: [PERMISSIONS.DOCUMENT_READ, PERMISSIONS.DOCUMENT_UPLOAD] },
    { key: '/reports', icon: <BarChartOutlined />, label: 'Reports', permissions: [PERMISSIONS.REPORT_VIEW, PERMISSIONS.VIEW_ANALYTICS] },
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getAccessibleMenuItems = useCallback((): MenuItem[] => {
    if (!currentUser) return [];
    return allMenuItems.filter((item) => {
      if (!item.permissions || item.permissions.length === 0) return true;
      return hasAnyPermission(item.permissions);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const menuItems = getAccessibleMenuItems();

  const getRoleInitials = () => {
    const parts = roleLabel.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return roleLabel.substring(0, 2).toUpperCase();
  };

  const handleMenuClick = (key: string) => {
    if (key === '/pos') {
      navigate('/pos/cart');
    } else if (key === '/vendor-products') {
      navigate({ pathname: '/vendor-products' });
    } else {
      navigate(key);
    }
    if (isMobile) {
      setCollapsed(true);
    }
  };

  const isActive = (key: string) => {
    const path = location.pathname || '';
    if (key === '/dashboard') return path === '/dashboard' || path === '/dashboard/';
    if (key === '/pos') return path.startsWith('/pos');
    if (key === '/vendor-products') {
      return path === '/vendor-products' || path.startsWith('/vendor-products/');
    }
    if (key === '/vendors') {
      return path === key || path.startsWith(key + '/');
    }
    return path === key || path.startsWith(key + '/');
  };

  return (
    <>
      {!collapsed && isMobile && (
        <div
          className="admin-sidebar-backdrop"
          onClick={() => {
            setCollapsed(true);
            onCollapse?.(true);
          }}
        />
      )}
      <div className={`admin-sidebar ${collapsed ? 'collapsed' : ''} ${isMobile ? 'admin-sidebar-mobile' : ''}`}>
        <div className="admin-sidebar-header">
          <div className="admin-sidebar-logo">
            {companyLogo ? (
              <div className="admin-logo-imageWrap">
                <Image
                  src={companyLogo}
                  alt="Company Logo"
                  preview={false}
                  className="admin-logo-image"
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                />
              </div>
            ) : (
              <div className="admin-logo-imageWrap">
                <img src={nacLogoUrl} alt="NAC Logo" className="admin-logo-image" />
              </div>
            )}
          </div>
          {!collapsed && (
            <div className="admin-sidebar-title-section">
              <div className="admin-sidebar-title">{panelTitle}</div>
              <div className="admin-sidebar-subtitle">{sectionSubtitle}</div>
            </div>
          )}
          <button
            type="button"
            className="admin-sidebar-close"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? 'Open menu' : 'Close sidebar'}
          >
            <span className="admin-sidebar-close-icon">
              {collapsed ? <MenuOutlined /> : <CloseOutlined />}
            </span>
          </button>
        </div>

        <div className="admin-sidebar-menu">
          {menuItems.map((item) => (
            <div
              key={item.key}
              className={`admin-menu-item ${isActive(item.key) ? 'active' : ''}`}
              onClick={() => handleMenuClick(item.key)}
            >
              <span className="admin-menu-icon">{item.icon}</span>
              {!collapsed && <span className="admin-menu-label">{item.label}</span>}
            </div>
          ))}
        </div>

        {!collapsed && (
          <div className="admin-sidebar-footer">
            <div className="admin-user-icon">{getRoleInitials()}</div>
            <div className="admin-user-info">
              <div className="admin-user-name">{currentUser?.name || 'User'}</div>
              <div className="admin-user-email">{(currentUser?.email || '').replace(/^["']|["']$/g, '')}</div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;
